/**
 * Google Ads Enhanced Conversions helpers (browser).
 *
 * Hashes PII client-side per Google Enhanced Conversions normalisation rules.
 * Never log raw PII or hashes from call sites.
 */

const SHA256_HEX_PATTERN = /^[a-f0-9]{64}$/

/**
 * Advertising consent that grants ad_user_data (meta stamped when advertising cookies allowed).
 */
export function hasAdUserDataConsent(doc = typeof document !== 'undefined' ? document : null)
{
	if (!doc || typeof doc.querySelector !== 'function')
	{
		return false
	}

	return doc.querySelector('meta[name=advertising_cookies_allowed]') !== null
}

/**
 * Trim and lower-case. Gmail/Googlemail: drop dots in the local-part.
 */
export function normaliseEmail(email)
{
	if (typeof email !== 'string')
	{
		return ''
	}

	const trimmed = email.trim().toLowerCase()

	const atPosition = trimmed.lastIndexOf('@')
	if (atPosition <= 0 || atPosition !== trimmed.indexOf('@') || atPosition === trimmed.length - 1)
	{
		return ''
	}

	const domain = trimmed.slice(atPosition + 1)
	let local = trimmed.slice(0, atPosition)

	if (local === '' || domain === '')
	{
		return ''
	}

	if (domain === 'gmail.com' || domain === 'googlemail.com')
	{
		local = local.replace(/\./g, '')
	}

	return `${local}@${domain}`
}

/**
 * Digits-only E.164-ish number including leading country code (no plus in the digit string;
 * the plus is re-added before hashing per Google rules).
 */
export function normalisePhoneDigits(phone, defaultCountryCode = '44')
{
	if (typeof phone !== 'string' && typeof phone !== 'number')
	{
		return ''
	}

	let digits = String(phone).replace(/\D+/g, '')

	if (digits === '')
	{
		return ''
	}

	if (digits.startsWith('00'))
	{
		digits = digits.slice(2)
	}

	if (digits.startsWith('0') && !digits.startsWith(defaultCountryCode))
	{
		digits = defaultCountryCode + digits.slice(1)
	}

	// Reject obviously incomplete numbers (E.164 max 15 digits; UK mobiles ~12 with country).
	if (digits.length < 10 || digits.length > 15)
	{
		return ''
	}

	return digits
}

/**
 * Trim and lower-case while preserving meaningful spaces and punctuation.
 */
export function normaliseNameOrStreet(value)
{
	if (typeof value !== 'string')
	{
		return ''
	}

	return value.trim().toLowerCase()
}

export function normalisePostalCode(value)
{
	if (typeof value !== 'string')
	{
		return ''
	}

	return value.trim().toLowerCase()
}

export function isSha256Hex(value)
{
	return typeof value === 'string' && SHA256_HEX_PATTERN.test(value)
}

/**
 * SHA-256 hex digest. Returns '' when crypto is unavailable or value empty.
 * Skips hashing when the value already looks like a SHA-256 hex digest (avoids double-hash).
 */
export async function sha256Hex(value, subtle = globalThis.crypto?.subtle)
{
	if (typeof value !== 'string' || value === '')
	{
		return ''
	}

	if (isSha256Hex(value))
	{
		return value
	}

	if (!subtle || typeof subtle.digest !== 'function')
	{
		return ''
	}

	const digest = await subtle.digest('SHA-256', new TextEncoder().encode(value))

	return Array.from(new Uint8Array(digest))
		.map((byte) => byte.toString(16).padStart(2, '0'))
		.join('')
}

/**
 * Build gtag Enhanced Conversions user_data, or null when consent denied / nothing usable.
 *
 * @param {object} order Order-like object (email, billing_*, etc.)
 * @param {{ document?: Document, subtle?: SubtleCrypto, defaultCountryCode?: string }} [options]
 * @returns {Promise<object|null>}
 */
export async function buildEnhancedConversionUserData(order, options = {})
{
	const doc = options.document ?? (typeof document !== 'undefined' ? document : null)

	if (!hasAdUserDataConsent(doc))
	{
		return null
	}

	if (!order || typeof order !== 'object')
	{
		return null
	}

	const subtle = options.subtle ?? globalThis.crypto?.subtle
	const defaultCountryCode = options.defaultCountryCode ?? '44'
	const userData = {}

	const email = normaliseEmail(order.email ?? '')
	if (email !== '')
	{
		const hashedEmail = await sha256Hex(email, subtle)
		if (hashedEmail !== '')
		{
			userData.sha256_email_address = hashedEmail
		}
	}

	const phoneDigits = normalisePhoneDigits(
		order.billing_telephone ?? order.shipping_telephone ?? order.telephone ?? '',
		defaultCountryCode
	)
	if (phoneDigits !== '')
	{
		const hashedPhone = await sha256Hex(`+${phoneDigits}`, subtle)
		if (hashedPhone !== '')
		{
			userData.sha256_phone_number = hashedPhone
		}
	}

	const address = {}
	const firstName = normaliseNameOrStreet(order.billing_forename ?? order.shipping_forename ?? '')
	const lastName = normaliseNameOrStreet(order.billing_surname ?? order.shipping_surname ?? '')
	const street = normaliseNameOrStreet(order.billing_address1 ?? order.shipping_address1 ?? '')
	const city = normaliseNameOrStreet(order.billing_town ?? order.shipping_town ?? '')
	const region = normaliseNameOrStreet(order.billing_county ?? order.shipping_county ?? '')
	const postalCode = normalisePostalCode(order.billing_postcode ?? order.shipping_postcode ?? '')

	if (firstName !== '')
	{
		const hashed = await sha256Hex(firstName, subtle)
		if (hashed !== '')
		{
			address.sha256_first_name = hashed
		}
	}

	if (lastName !== '')
	{
		const hashed = await sha256Hex(lastName, subtle)
		if (hashed !== '')
		{
			address.sha256_last_name = hashed
		}
	}

	if (street !== '')
	{
		const hashed = await sha256Hex(street, subtle)
		if (hashed !== '')
		{
			address.sha256_street = hashed
		}
	}

	// City / region / postal / country are sent in clear per Google Enhanced Conversions rules.
	if (city !== '')
	{
		address.city = city
	}

	if (region !== '')
	{
		address.region = region
	}

	if (postalCode !== '')
	{
		address.postal_code = postalCode
	}

	address.country = 'GB'

	if (Object.keys(address).length > 1)
	{
		userData.address = address
	}

	return Object.keys(userData).length > 0 ? userData : null
}

/**
 * Merge user_data onto a purchase (or other) event params object when consent allows.
 * Returns a shallow copy; never mutates the original. Omits user_data when unavailable.
 */
export async function withEnhancedConversionUserData(eventParams, order, options = {})
{
	const params = eventParams && typeof eventParams === 'object' ? { ...eventParams } : {}

	let userData
	try
	{
		userData = await buildEnhancedConversionUserData(order, options)
	}
	catch
	{
		// Enhanced Conversions are best-effort and must never suppress the core event.
		return params
	}

	if (userData)
	{
		params.user_data = userData
	}

	return params
}
